import React, { useEffect, useRef, useState } from "react";
import { Alert, Spin } from "antd";

// Rich text editor: the same CKEditor 5 build the old Education web used
// (bold, italic, underline, link, lists, tables, colours, font size,
// alignment, images), so the saved HTML looks the same in the app's web view.
// The build is served from public/vendor/ckeditor5 and loaded on first use,
// so it is not part of the app bundle.
//
// Works as an antd form field: <Form.Item name="..."><RichTextEditor /></Form.Item>.
// onUploadImage(file) uploads a picked image and resolves to its URL.

// Pasted text (Word, Google Docs, web pages) keeps its font, size, colours,
// headings, lists, tables, alignment and links: sizes and fonts accept any
// value, not only the toolbar's list.
const EDITOR_CONFIG = {
  toolbar: {
    items: [
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "link",
      "bulletedList",
      "numberedList",
      "outdent",
      "indent",
      "|",
      "insertTable",
      "horizontalLine",
      "imageInsert",
      "|",
      "fontFamily",
      "fontSize",
      "fontColor",
      "fontBackgroundColor",
      "alignment",
      "|",
      "undo",
      "redo",
    ],
  },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      { model: "heading1", view: "h2", title: "Heading 1", class: "ck-heading_heading1" },
      { model: "heading2", view: "h3", title: "Heading 2", class: "ck-heading_heading2" },
      { model: "heading3", view: "h4", title: "Heading 3", class: "ck-heading_heading3" },
    ],
  },
  fontSize: { options: ["default", 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36], supportAllValues: true },
  fontFamily: { supportAllValues: true },
};

const SCRIPT_URL = `${process.env.PUBLIC_URL || ""}/vendor/ckeditor5/ckeditor.js`;
let loading = null;

const loadEditorClass = () => {
  if (window.ClassicEditor) return Promise.resolve(window.ClassicEditor);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => (window.ClassicEditor ? resolve(window.ClassicEditor) : reject(new Error("CKEditor did not load")));
      script.onerror = () => reject(new Error("CKEditor could not be loaded"));
      document.head.appendChild(script);
      // the editor's pop-ups (link, image, table) sit above antd modals
      const style = document.createElement("style");
      style.textContent = ":root{--ck-z-default:1100;--ck-z-panel:calc(var(--ck-z-default) + 999);} .ck-editor__editable_inline{min-height:140px;}";
      document.head.appendChild(style);
    }).catch((error) => {
      loading = null;
      throw error;
    });
  }
  return loading;
};

// true when the HTML has no text, image or table (e.g. "<p>&nbsp;</p>")
export const isRichTextEmpty = (html) => {
  if (!html) return true;
  if (/<(img|table|iframe)\b/i.test(html)) return false;
  return !html.replace(/<[^>]*>/g, "").replace(/&nbsp;|\s/g, "");
};

const RichTextEditor = ({ value, onChange, onUploadImage, placeholder }) => {
  const holder = useRef(null);
  const editorRef = useRef(null);
  const latest = useRef({ value, onChange, onUploadImage });
  latest.current = { value, onChange, onUploadImage };
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    loadEditorClass()
      .then((ClassicEditor) => {
        if (cancelled || !holder.current) return null;
        return ClassicEditor.create(holder.current, {
          ...EDITOR_CONFIG,
          placeholder,
          // a plugin must be a regular function: CKEditor creates it with "new"
          extraPlugins: [
            function UploadAdapterPlugin(editor) {
              editor.plugins.get("FileRepository").createUploadAdapter = (loader) => ({
                upload: () =>
                  loader.file.then((file) => {
                    if (!latest.current.onUploadImage) return Promise.reject(new Error("Image upload is not available here."));
                    return latest.current.onUploadImage(file).then((url) => ({ default: url }));
                  }),
                abort: () => {},
              });
            },
          ],
        });
      })
      .then((editor) => {
        if (!editor) return;
        if (cancelled) return editor.destroy();
        editorRef.current = editor;
        editor.setData(latest.current.value || "");
        editor.model.document.on("change:data", () => {
          const data = editor.getData();
          if (data !== latest.current.value && latest.current.onChange) latest.current.onChange(data);
        });
        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("failed"));
    return () => {
      cancelled = true;
      if (editorRef.current) editorRef.current.destroy().catch(() => {});
      editorRef.current = null;
    };
    // created once; later value changes come from the editor itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // a value set from outside (e.g. form reset) is shown in the editor
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && (value || "") !== editor.getData()) editor.setData(value || "");
  }, [value]);

  return (
    <div>
      {status === "loading" && <Spin size="small" style={{ margin: "8px 0" }} />}
      {status === "failed" && <Alert type="error" showIcon message="The text editor could not be loaded. Please refresh the page." />}
      <div ref={holder} />
    </div>
  );
};

export default RichTextEditor;
